import { LockClosedIcon } from '@heroicons/react/outline';
import { joiResolver } from '@hookform/resolvers/joi';
import loginValidationSchema from 'applications/web/validation/login';
import Aldo from 'assets/svg/Aldo.svg';
import Mail from 'assets/svg/icons/Mail';
import AccountableLogoWithSlugan from 'components/atoms/AccountableLogoWithSlugan.component';
import { Alert } from 'components/atoms/Alert';
import { Button } from 'components/atoms/Button';
import { FieldConnector } from 'components/atoms/Form/FieldConnector';
import FreeTextInput from 'components/atoms/Form/FreeTextInput';
import Link from 'components/atoms/Link';
import AppleAuthButton from 'components/molecules/AppleAuthButton';
import GoogleAuthButton from 'components/molecules/GoogleAuthButton';
import AuthPageLayout from 'components/templates/AuthPageLayout';
import useLogin from 'hooks/Authentication/useLogin';
import getIntercom from 'hooks/intercom.hook';
import _get from 'lodash/get';
import { AnimatePresence, motion } from 'motion/react';
import { useCallback, useRef, useState, type FC } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { getTracingEvents } from 'utils/amplitude';

import {
  WEBAPP_LOGIN_APPLE_ACCOUNT,
  WEBAPP_LOGIN_CLICKEDLOGIN,
  WEBAPP_LOGIN_CLICKEDLOGINEMAILFIELD,
  WEBAPP_LOGIN_CLICKEDLOGINPASSWORDFIELD,
  WEBAPP_LOGIN_CLICKEDONCREATEACCOUNT,
  WEBAPP_LOGIN_CLICKEDOPENINTERCOM,
  WEBAPP_LOGIN_CLICKEDRESETPASSWORDLINK,
  WEBAPP_LOGIN_GOOGLE_ACCOUNT,
} from 'utils/amplitude/events/login.amplitude';
import { ArrowRightIcon } from 'utils/icons';

const tracingEvents = getTracingEvents({
  onLogin: {
    name: WEBAPP_LOGIN_CLICKEDLOGIN,
    data: (email: string) => ({ email }),
  },
  onClickCreateNewAccount: WEBAPP_LOGIN_CLICKEDONCREATEACCOUNT,
  onClickResetPasswordLink: WEBAPP_LOGIN_CLICKEDRESETPASSWORDLINK,
});

const UserLogin: FC = () => {
  const form = useForm({
    defaultValues: { email: '', password: '' },
    resolver: joiResolver(loginValidationSchema),
  });

  const {
    handleSubmit,
    formState: { errors },
  } = form;

  const [globalLoginError, setGlobalLoginError] = useState('');

  const { t } = useTranslation();

  const { mutateAsync, isLoading } = useLogin();

  const { showIntercom } = getIntercom();

  const onSSOLogin = useCallback(
    async (idToken: string, providerKey: 'googleIdToken' | 'appleIdToken') => {
      if (!idToken) return;

      mutateAsync({
        [providerKey]: idToken,
        clientId: localStorage.getItem('clientId') || undefined,
      }).catch((e: any) => {
        const accountNotConnected = !!_get(e, 'response.data.errors', []).find(
          (e: { message: string; code: string }) =>
            e.code === 'no-account-connected',
        );

        if (accountNotConnected) {
          setGlobalLoginError(
            t('login.sso.no_account_connected_error', {
              provider: providerKey === 'appleIdToken' ? 'Apple' : 'Google',
            }),
          );
        }
      });
    },
    [mutateAsync, t],
  );

  const ssoContainerRef = useRef(null);

  return (
    <AuthPageLayout
      title={t('login.submit.web')}
      subTitle={`${t('login.instruction_part_1')} ${t(
        'login.instruction_part_2',
      )}`}
      renderAside={() => {
        return (
          <AnimatePresence>
            <div className="flex flex-col items-center">
              <AccountableLogoWithSlugan animate />

              <motion.div
                className="h-[319px] w-[275px] bg-no-repeat absolute right-0"
                initial={{ opacity: 0, bottom: '-100%' }}
                animate={{ opacity: 1, bottom: 0 }}
                transition={{ duration: 0.5, delay: 1.2 }}
                style={{ backgroundImage: `url(${Aldo})` }}
              ></motion.div>
            </div>
          </AnimatePresence>
        );
      }}
    >
      <FormProvider {...form}>
        <form
          onSubmit={handleSubmit((values) => {
            tracingEvents.onLogin(values?.email);
            mutateAsync({
              password: values.password,
              email: values.email,
              clientId: localStorage.getItem('clientId') || undefined, // clientId has to be persisted and sent over with login & logout requests
            });
          })}
        >
          {globalLoginError ? (
            <Alert
              type="warning"
              description={globalLoginError}
              className="mt-4"
            />
          ) : null}

          <div ref={ssoContainerRef} className="mt-6">
            <GoogleAuthButton
              containerRef={ssoContainerRef}
              onSuccess={async (credentialResponse: any) =>
                await onSSOLogin(
                  credentialResponse.credential as string,
                  'googleIdToken',
                )
              }
              label={t('login.sso.google')}
              tracingEvent={WEBAPP_LOGIN_GOOGLE_ACCOUNT}
            />

            <AppleAuthButton
              onSuccess={async (response: any) =>
                await onSSOLogin(
                  response?.authorization?.id_token as string,
                  'appleIdToken',
                )
              }
              label={t('login.sso.apple')}
              tracingEvent={WEBAPP_LOGIN_APPLE_ACCOUNT}
            />
          </div>

          <div className="flex items-center mt-2">
            <div className="h-[1px] bg-primary-100 w-[40%]" />

            <span className="flex-1 text-center capitalize font-semibold text-primary-500">
              {t('or')}
            </span>

            <div className="h-[1px] bg-primary-100 w-[40%]" />
          </div>

          <div className="my-4">
            <FieldConnector
              label={t('login.email')}
              errors={errors}
              name="email"
            >
              {({ field }) => (
                <FreeTextInput
                  {...field}
                  Icon={Mail}
                  iconClassName="p-0.5"
                  placeholder={t('login.emailPlaceholder')}
                  tracing={{
                    onFocus: WEBAPP_LOGIN_CLICKEDLOGINEMAILFIELD,
                  }}
                />
              )}
            </FieldConnector>
          </div>
          <div className="my-4">
            <FieldConnector
              label={t('login.password')}
              errors={errors}
              name="password"
            >
              {({ field }) => (
                <FreeTextInput
                  {...field}
                  iconClassName="p-0.5"
                  type="password"
                  Icon={LockClosedIcon}
                  placeholder={t('login.passwordPlaceholder')}
                  tracing={{ onFocus: WEBAPP_LOGIN_CLICKEDLOGINPASSWORDFIELD }}
                />
              )}
            </FieldConnector>
          </div>

          <Button
            color="taxes"
            type="submit"
            loading={isLoading}
            icon={ArrowRightIcon}
            iconPlacement="right"
            className="!w-full h-12"
          >
            {t('login.submit.web')}
          </Button>

          <div className="mt-4 flex flex-col gap-8">
            <div className="flex justify-center gap-2 items-center">
              <Link
                href="/reset"
                onClick={() => tracingEvents.onClickResetPasswordLink()}
                className="cursor-pointer whitespace-nowrap pb-0 mb-0 !text-primary"
              >
                {t('login.forgot_your_password')}
              </Link>
            </div>
            <div className="flex justify-center items-center gap-2">
              <p className="text-primary-600 font-semibold whitespace-nowrap !text-base">
                {t('login.no_account_yet')}
              </p>
              <Link
                href="/signup"
                className="cursor-pointer !font-normal flex whitespace-nowrap !text-base items-center gap-2 pb-0 mb-0 !text-taxes"
                onClick={() => tracingEvents.onClickCreateNewAccount()}
              >
                {t('login.sign_up')}
                <ArrowRightIcon className="w-4 h-4" />
              </Link>
            </div>

            <div className="mt-[4.25rem] flex justify-center !font-semibold gap-2 items-center">
              <p className="text-sm text-primary-600">{t('need_help')}</p>
              <Button
                structure="text"
                className="text-primary-600"
                onClick={showIntercom}
                tracingEvent={WEBAPP_LOGIN_CLICKEDOPENINTERCOM}
              >
                <div className="first-letter:capitalize">{t('contact_us')}</div>
              </Button>
            </div>
          </div>
        </form>
      </FormProvider>
    </AuthPageLayout>
  );
};
export default UserLogin;
